import { useQuery } from '@tanstack/react-query';
import apiClient from '../../../lib/apiClient';

/**
 * Fetches a bulk lookup of which ZT capabilities, PDP signals, and the PDP
 * itself have real procurement data backing them — used to decide whether
 * to render the $ drill-in icon next to each row.
 *
 * Returns: {
 *   capabilities: { [capCode]: Indicator },
 *   signals:      { [signalId]: Indicator },
 *   ...
 * }
 *
 * Indicator shape:
 *   hasSpend              — any active or recent (within lookbackYears) PO
 *   hasExpiringContract   — at least one row with end_date in the future
 *   totalSpend            — $ sum of detected rows' line_total
 *   transactionCount      — how many PO rows matched
 *   activeContractCount   — rows with end_date in the future
 *   nextExpiration        — earliest upcoming end_date (ISO string) or null
 *   topVendor, topProduct — strongest match, matching the suggestion banner
 *
 * Disabled when `departmentId` is falsy so the ZT page can render without a
 * selected department without blasting the API.
 */
export function useSpendIndicators(departmentId, { lookbackYears = 2 } = {}) {
  return useQuery({
    queryKey: ['zt-spend-indicators', departmentId, lookbackYears],
    enabled: !!departmentId,
    queryFn: async () => {
      const qs = `?lookback_years=${encodeURIComponent(lookbackYears)}`;
      return apiClient.get(
        `/api/zero-trust/departments/${departmentId}/spend-indicators${qs}`
      );
    },
    // Procurement data changes on daily cadence — a 5-minute staleTime keeps
    // the icons responsive to ingest without hammering the endpoint.
    staleTime: 5 * 60_000,
  });
}

/**
 * Pure selector: given the indicators payload and a scope, return the entry
 * (or an empty default shape when the payload hasn't loaded yet). Keeps the
 * render sites simple — they don't need to guard every field access.
 */
export function selectIndicator(indicators, scope, key) {
  if (!indicators || !scope || !key) return EMPTY_INDICATOR;
  const bucket = indicators[scope];
  if (!bucket) return EMPTY_INDICATOR;
  return bucket[key] || EMPTY_INDICATOR;
}

/**
 * Vendor / product-level lookup so render sites can stamp a $ next to any
 * vendor reference on the ZT page — overlay rows, assigned vendor labels,
 * AI suggestions, PDP vendor text.
 *
 * Prefers the tighter product-level indicator when both vendor and product
 * are supplied, falling back to vendor-level if we don't have a product-
 * level match (common when the display string is "Okta" without a product).
 *
 * Options:
 *   requireProductMatch: if true, returns null when productName is given
 *     but no product-level match is found (prevents misleading vendor totals)
 *
 * Returns `null` when there's no spend or active contract — callers render
 * nothing in that case, matching how SpendDrillButton handles an empty
 * indicator. Callers can treat `null` as "no $ icon".
 */
export function selectVendorIndicator(indicators, vendorName, productName = null, options = {}) {
  const { requireProductMatch = false } = options;
  if (!indicators || !vendorName) return null;
  const vendors = indicators.vendors;
  if (!vendors) return null;
  const v = vendors[String(vendorName).toLowerCase()];
  if (!v) return null;

  if (productName) {
    // First try exact match, then a fuzzy contains match — so e.g.
    // "Okta Identity Cloud" in the UI lines up with "Okta" scoped spend
    // when the PO data only records vendor-level products.
    const pKey = String(productName).toLowerCase();
    const exact = v.products?.[pKey];
    if (exact && (exact.hasSpend || exact.hasExpiringContract)) {
      return toVendorIndicator(exact, v.vendor, exact.product, true);
    }
    const fuzzy = Object.values(v.products || {}).find(p => {
      const pn = String(p.product || '').toLowerCase();
      return pn && (pKey.includes(pn) || pn.includes(pKey));
    });
    if (fuzzy && (fuzzy.hasSpend || fuzzy.hasExpiringContract)) {
      return toVendorIndicator(fuzzy, v.vendor, fuzzy.product, true);
    }
    // If product was requested but not found, and requireProductMatch is true,
    // don't fall back to vendor-level (prevents misleading "$13M for Duo" when it's all Cisco)
    if (requireProductMatch) return null;
  }

  if (!v.hasSpend && !v.hasExpiringContract) return null;
  return toVendorIndicator(v, v.vendor, null, false);
}

function toVendorIndicator(src, vendor, product, isProductLevel = false) {
  return {
    hasSpend: !!src.hasSpend,
    hasExpiringContract: !!src.hasExpiringContract,
    totalSpend: src.totalSpend || 0,
    transactionCount: src.transactionCount || 0,
    activeContractCount: src.activeContractCount || 0,
    nextExpiration: src.nextExpiration || null,
    topVendor: vendor,
    topProduct: product,
    isProductLevel, // true = product-specific spend, false = vendor-level (may include unrelated products)
  };
}

/**
 * Parse a display vendor string like "Okta (Okta Identity Cloud)" or
 * "SailPoint (IdentityNow)" into { vendor, product }. Falls back to
 * { vendor: raw, product: null } when the paren pattern doesn't match.
 */
export function parseVendorDisplay(raw) {
  if (!raw || typeof raw !== 'string') return { vendor: '', product: null };
  const match = raw.match(/^([^(]+?)\s*(?:\(([^)]+)\))?\s*$/);
  if (!match) return { vendor: raw.trim(), product: null };
  return {
    vendor: (match[1] || '').trim(),
    product: match[2] ? match[2].trim() : null,
  };
}

export const EMPTY_INDICATOR = Object.freeze({
  hasSpend: false,
  hasExpiringContract: false,
  totalSpend: 0,
  transactionCount: 0,
  activeContractCount: 0,
  nextExpiration: null,
  topVendor: null,
  topProduct: null,
});
